import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import type {
    ExportSettingsSourceType,
    SalesforcePreDefinedType
} from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type {
    DynamicsPreDefinedType,
    HubspotPreDefinedType
} from "app/pages/sales-intelligence/sub-modules/management/store/types/shared";
import {
    selectHsExcludeStatusFields,
    selectHsIsLoadingExcludeStatusFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectSfExcludeStatusFields,
    selectSfIsLoadingExcludeStatusFields,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import {
    getSfExcludeStatusFieldsThunk
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/exclude";
import {
    getHsExcludeStatusFieldsThunk
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/exclude";

import type { CrmProvider } from "../types/types";
import {
    selectDynamicsExcludeStatusFields,
    selectDynamicsIsLoadingExcludeStatusFields
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { CrmFieldType } from "app/pages/sales-intelligence/common-components/export-table/types";

type FetchCrmFieldsParams = {
    columnName: string;
    sourceType: ExportSettingsSourceType;
    isPreDefined: boolean;
    preDefinedType: SalesforcePreDefinedType | HubspotPreDefinedType | DynamicsPreDefinedType;
};

const CRM_SELECTORS: Record<CrmIntegrationProvider, any> = {
    salesforce: {
        crmFieldsSelector: selectSfExcludeStatusFields,
        isLoadingSelector: selectSfIsLoadingExcludeStatusFields,
    },
    hubspot: {
        crmFieldsSelector: selectHsExcludeStatusFields,
        isLoadingSelector: selectHsIsLoadingExcludeStatusFields,
    },
    dynamics: {
        crmFieldsSelector: selectDynamicsExcludeStatusFields,
        isLoadingSelector: selectDynamicsIsLoadingExcludeStatusFields,
    },
} as const;

const useCrmFieldsProvider = (crmProvider: CrmProvider) => {
    const dispatch = useDispatch();

    const { crmFieldsSelector, isLoadingSelector } = CRM_SELECTORS[crmProvider];

    const crmOptions: CrmFieldType[] = useSelector(crmFieldsSelector);
    const isCrmFieldsLoading: boolean = useSelector(isLoadingSelector);

    const fetchCrmFields = useCallback(
        ({ columnName, sourceType, isPreDefined, preDefinedType }: FetchCrmFieldsParams): void => {
            if (crmProvider === "salesforce") {
                dispatch(
                    getSfExcludeStatusFieldsThunk({
                        columnName,
                        isPreDefined,
                        type: preDefinedType as SalesforcePreDefinedType,
                        sourceType,
                    }),
                );
            } else {
                dispatch(
                    getHsExcludeStatusFieldsThunk({
                        columnName,
                        isPreDefined,
                        type: preDefinedType as HubspotPreDefinedType,
                        sourceType,
                    }),
                );
            }
        },
        [crmProvider, dispatch],
    );

    return {
        crmOptions,
        isCrmFieldsLoading,
        fetchCrmFields,
    };
};

export default useCrmFieldsProvider;
